// Session 27: one lesson (/courses/:courseKey/:lessonKey).
//
// Off      placeholder, same as the rest of a locked service
// Preview  only the free sample lesson opens, and it is read-only. Any other
//          lesson redirects to the course page rather than 404ing, because the
//          reader got there from a list that showed it as locked
// On       the full lesson, with progress

import { useCallback, useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import Placeholder from './Placeholder.jsx';
import { courseMode } from '../modules/index.js';
import { courseByKey, lessonByKey, isSampleLesson } from '../courses/index.js';
import LessonRenderer from '../components/course/LessonRenderer.jsx';

export default function CourseLesson() {
  const { courseKey, lessonKey } = useParams();
  const { entitlements, activeId, isAgency, me } = useAuth();
  const [complete, setComplete] = useState(false);
  const [checked, setChecked] = useState(false);

  const course = courseByKey(courseKey);
  const lesson = course ? lessonByKey(course, lessonKey) : null;
  const mode = courseMode(entitlements);
  const readOnly = mode === 'preview';

  const load = useCallback(async () => {
    if (!course || !lesson || readOnly || !me?.id) { setChecked(true); return; }
    const { data } = await supabase
      .from('v_portal_course_progress').select('*')
      .eq('course_key', course.key).eq('lesson_key', lesson.key);
    const mine = (data || []).find((r) => r.user_id === me.id);
    setComplete(mine?.status === 'complete');
    setChecked(true);
  }, [course, lesson, readOnly, me?.id]);

  useEffect(() => { setChecked(false); load(); }, [load]);

  if (!course) return <Navigate to="/" replace />;
  if (mode === 'off') return <Placeholder />;

  // A lesson that is still being written, or a preview reader on a locked
  // lesson: send them back to the list they came from.
  const courseHref = clientPath(`/courses/${course.key}`, activeId);
  if (!lesson || lesson.status !== 'ready') return <Navigate to={courseHref} replace />;
  if (readOnly && !isSampleLesson(course, lesson.key)) return <Navigate to={courseHref} replace />;

  if (!checked) return <p className="lede">Loading…</p>;

  return (
    <LessonRenderer
      course={course}
      lesson={lesson}
      readOnly={readOnly}
      isAgency={isAgency}
      isComplete={complete}
      onCompleted={load}
      clientQuery={activeId ? `?client=${encodeURIComponent(activeId)}` : ''}
    />
  );
}
